import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useState } from "react";

const OnboardingTutorials = () => {
  const { t, i18n } = useTranslation();

  const [selected, setSelected] = useState<"company" | "employee">("company");
  const [isFullscreen, setIsFullscreen] = useState(false);

  const lang = i18n.language?.split("-")[0] || "en";
  const companyOnboardingUrl = `https://minio.gnzabe.com/registration-bucket/${lang}/company/story.html`;
  const employeeOnboardingUrl = `https://minio.gnzabe.com/registration-bucket/${lang}/user/story.html`;

  return (
    <div className="min-h-screen bg-white text-gray-900 transition-colors duration-300 dark:bg-gray-900 dark:text-gray-100">
      <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">
            {t("onboarding.tutorials.title", {
              defaultValue: "Onboarding Tutorials",
            })}
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-300">
            {t("onboarding.tutorials.subtitle", {
              defaultValue:
                "Choose a tutorial below to learn how to register as a company or an employee.",
            })}
          </p>
        </div>

        <div className="mb-4 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => setSelected("company")}
            className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
              selected === "company"
                ? "bg-primary-600 text-white dark:bg-primary-500"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-white/10 dark:text-gray-200 dark:hover:bg-white/15"
            }`}
          >
            {t("onboarding.tutorials.company", {
              defaultValue: "Company Onboarding",
            })}
          </button>
          <button
            type="button"
            onClick={() => setSelected("employee")}
            className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
              selected === "employee"
                ? "bg-primary-600 text-white dark:bg-primary-500"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-white/10 dark:text-gray-200 dark:hover:bg-white/15"
            }`}
          >
            {t("onboarding.tutorials.employee", {
              defaultValue: "Employee Onboarding",
            })}
          </button>

          <button
            type="button"
            onClick={() => setIsFullscreen((v) => !v)}
            className={`ml-auto rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
              isFullscreen
                ? "bg-gray-200 text-gray-900 hover:bg-gray-300 dark:bg-white/20 dark:text-white dark:hover:bg-white/25"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-white/10 dark:text-gray-200 dark:hover:bg-white/15"
            }`}
            aria-label={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
          >
            {isFullscreen
              ? t("onboarding.tutorials.exitFullscreen", {
                  defaultValue: "Exit Full Screen",
                })
              : t("onboarding.tutorials.fullscreen", {
                  defaultValue: "Full Screen",
                })}
          </button>
        </div>

        <div
          className={`${
            isFullscreen
              ? "fixed inset-0 z-50 m-0 rounded-none border-0 bg-white dark:bg-gray-900"
              : "rounded-xl border border-gray-200 shadow-sm dark:border-white/10"
          } overflow-hidden`}
        >
          {isFullscreen && (
            <div className="absolute right-4 top-4 z-10">
              <button
                type="button"
                onClick={() => setIsFullscreen(false)}
                className="rounded-md bg-gray-900/70 px-3 py-1.5 text-xs font-medium text-white shadow hover:bg-gray-900/80 focus:outline-none"
                aria-label="Close Fullscreen"
              >
                {t("onboarding.tutorials.close", { defaultValue: "Close" })}
              </button>
            </div>
          )}
          <iframe
            key={`${selected}-${lang}`}
            src={
              selected === "company"
                ? companyOnboardingUrl
                : employeeOnboardingUrl
            }
            title={
              selected === "company"
                ? "Company Onboarding"
                : "Employee Onboarding"
            }
            className={`${
              isFullscreen ? "h-screen" : "h-[70vh]"
            } w-full bg-white dark:bg-gray-900`}
            loading="lazy"
            referrerPolicy="no-referrer"
            allowFullScreen
          />
        </div>

        <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
          {t("onboarding.tutorials.openInNewTab", {
            defaultValue: "If the tutorial does not load, you can",
          })}{" "}
          <a
            href={
              selected === "company"
                ? companyOnboardingUrl
                : employeeOnboardingUrl
            }
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary-600 underline dark:text-primary-400"
          >
            {t("onboarding.tutorials.openHere", {
              defaultValue: "open it here",
            })}
          </a>
          .
        </p>

        <div className="mt-10">
          <Link
            to="/"
            className="text-primary-600 hover:underline dark:text-primary-400"
          >
            {t("common.backToHome", { defaultValue: "Back to Home" })}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default OnboardingTutorials;
